import React from 'react';
import { DataConnectionType, ConnectionValidationResult } from '../types';
import { getProviderDisplayName, isFileProvider } from '../utilities';
import { ConnectionTypeModel } from '../types';
import DataSourceFileBrowserDialog from './DataSourceFileBrowserDialog';

interface ConnectionDataStepProps {
    type?: DataConnectionType;
    connectionTypes: ConnectionTypeModel[];
    config: Record<string, unknown>;
    errors: Record<string, string>;
    testResult: ConnectionValidationResult | null;
    onConfigChange: (key: string, value: unknown) => void;
}

const getStringValue = (config: Record<string, unknown>, key: string) => String(config[key] ?? '');
const getBoolValue = (config: Record<string, unknown>, key: string) => config[key] === true;

const ConnectionDataStep: React.FC<ConnectionDataStepProps> = ({
    type,
    connectionTypes,
    config,
    errors,
    testResult,
    onConfigChange,
}) => {
    const [fileBrowserOpen, setFileBrowserOpen] = React.useState(false);
    const title = type
        ? InovxLoader.t('facility:dataSources.connectionDataFor', { provider: getProviderDisplayName(connectionTypes, type) })
        : InovxLoader.t('facility:dataSources.steps.connectionData');
    const isWindowsAuth = type === DataConnectionType.MsSqlServer && getStringValue(config, 'authentication') === 'windows';
    const testResultMessage = testResult
        ? testResult.Message || (testResult.Successful ? InovxLoader.t('facility:dataSources.connectionSuccessful') : InovxLoader.t('facility:dataSources.connectionFailed'))
        : '';
    const showConnectionTestError = errors.connectionTest && errors.connectionTest !== testResultMessage;

    const renderTextField = (key: string, labelKey: string, inputType = 'text') => (
        <label className="data-sources-field">
            <span>{InovxLoader.t(labelKey)}</span>
            <input
                type={inputType}
                className={errors[key] ? 'invalid' : ''}
                value={getStringValue(config, key)}
                onChange={event => onConfigChange(key, event.target.value)}
            />
            {errors[key] && <span className="data-sources-field-error">{errors[key]}</span>}
        </label>
    );

    const renderCheckbox = (key: string, labelKey: string) => (
        <label className="data-sources-checkbox-field">
            <input
                type="checkbox"
                checked={getBoolValue(config, key)}
                onChange={event => onConfigChange(key, event.target.checked)}
            />
            <span>{InovxLoader.t(labelKey)}</span>
        </label>
    );

    const renderSqlLikeFields = () => (
        <div className="data-sources-sql-form-grid">
            <div className="data-sources-form-stack">
                {renderTextField('serverhost', 'facility:dataSources.fields.serverHost')}
                {renderTextField('port', 'facility:dataSources.fields.port', 'number')}
                {renderTextField('database', 'facility:dataSources.fields.database')}
                {type === DataConnectionType.MsSqlServer && (
                    <div className="data-sources-checkbox-group">
                        {renderCheckbox('encryptconnection', 'facility:dataSources.fields.encryptConnection')}
                        {renderCheckbox('trustservercertificate', 'facility:dataSources.fields.trustServerCertificate')}
                    </div>
                )}
            </div>
            <div className="data-sources-form-stack">
                {type === DataConnectionType.MsSqlServer && (
                    <label className="data-sources-field">
                        <span>{InovxLoader.t('facility:dataSources.fields.authentication')}</span>
                        <select value={getStringValue(config, 'authentication')} onChange={event => onConfigChange('authentication', event.target.value)}>
                            <option value="sql">{InovxLoader.t('facility:dataSources.authentication.sql')}</option>
                            <option value="windows">{InovxLoader.t('facility:dataSources.authentication.windows')}</option>
                        </select>
                    </label>
                )}
                {!isWindowsAuth && renderTextField('username', 'facility:dataSources.fields.username')}
                {!isWindowsAuth && renderTextField('password', 'facility:dataSources.fields.password', 'password')}
            </div>
        </div>
    );

    const renderFileFields = () => (
        <div className="data-sources-form-stack">
            <label className="data-sources-field">
                <span>{InovxLoader.t('facility:dataSources.fields.filePath')}</span>
                <div className="data-sources-file-path-row">
                    <input
                        type="text"
                        className={errors.filepath ? 'invalid' : ''}
                        value={getStringValue(config, 'filepath')}
                        onChange={event => onConfigChange('filepath', event.target.value)}
                    />
                    <button type="button" className="e-btn" disabled={!type} onClick={() => setFileBrowserOpen(true)}>
                        {InovxLoader.t('facility:dataSources.files.browse')}
                    </button>
                </div>
                {errors.filepath && <span className="data-sources-field-error">{errors.filepath}</span>}
            </label>
            {type === DataConnectionType.Csv && (
                <>
                    {renderCheckbox('hasheader', 'facility:dataSources.fields.hasHeader')}
                    {renderTextField('delimiter', 'facility:dataSources.fields.delimiter')}
                </>
            )}
        </div>
    );

    return (
        <div className="data-sources-step data-sources-connection-step">
            <div className="data-sources-step-title">{title}</div>
            {isFileProvider(type) ? renderFileFields() : renderSqlLikeFields()}
            {isFileProvider(type) && testResult?.Successful && (
                <div className="data-sources-help-text">{InovxLoader.t('facility:dataSources.schemaDiscoveryHost')}</div>
            )}
            {showConnectionTestError && <div className="data-sources-step-error">{errors.connectionTest}</div>}
            {fileBrowserOpen && type && (
                <DataSourceFileBrowserDialog
                    type={type}
                    visible={fileBrowserOpen}
                    onClose={() => setFileBrowserOpen(false)}
                    onSelect={path => {
                        onConfigChange('filepath', path);
                        setFileBrowserOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default ConnectionDataStep;
